/* Native quantity offers. Prices describe configured Shopify discounts;
   the existing product form remains responsible for adding the real variants. */
(function () {
  'use strict';

  const integer = (value, fallback = 0) => Number.isFinite(Number(value)) ? Math.floor(Number(value)) : fallback;
  const list = (nodes) => Array.from(nodes || []);
  const positive = (value, fallback = 0) => Math.max(0, Number(value) || fallback);

  function calculateTier(prices, discount = {}) {
    const amounts = prices.map((price) => Math.max(0, integer(price)));
    const total = amounts.reduce((sum, price) => sum + price, 0);
    const type = discount.type || 'none';
    const value = positive(discount.value);
    const ready = discount.ready === true;
    const rate = Number(discount.rate == null ? 1 : discount.rate);
    const fixedAllowed = discount.fixedAllowed !== false && Number.isFinite(rate) && rate > 0;
    const buyQuantity = Math.max(0, integer(discount.buyQuantity));
    const freeQuantity = Math.max(0, integer(discount.freeQuantity));
    const groupQuantity = buyQuantity + freeQuantity;
    const qualifiedFreeCount = type === 'buy_x_get_y' && buyQuantity > 0 && freeQuantity > 0
      ? Math.floor(amounts.length / groupQuantity) * freeQuantity : 0;
    const freeIndices = ready && qualifiedFreeCount > 0
      ? amounts.map((price, index) => ({ price, index }))
        .sort((first, second) => first.price - second.price || first.index - second.index)
        .slice(0, qualifiedFreeCount).map((item) => item.index) : [];
    let saving = 0;
    if (type === 'buy_x_get_y') {
      saving = freeIndices.reduce((sum, index) => sum + amounts[index], 0);
    } else if (ready && type === 'percent') {
      const percent = Math.min(100, value);
      saving = amounts.reduce((sum, price) => sum + Math.round(price * percent / 100), 0);
    } else if (ready && fixedAllowed && type === 'fixed_item') {
      const cents = Math.round(value * 100 * rate);
      saving = amounts.reduce((sum, price) => sum + Math.min(price, cents), 0);
    } else if (ready && fixedAllowed && type === 'fixed_total') {
      saving = Math.min(total, Math.round(value * 100 * rate));
    }
    saving = Math.min(total, Math.max(0, saving));
    return {
      quantity: amounts.length, total, saving, final: total - saving,
      unit: amounts.length ? Math.round((total - saving) / amounts.length) : 0,
      qualifiedFreeCount, freeCount: freeIndices.length, freeIndices,
      paidQuantity: amounts.length - freeIndices.length
    };
  }

  function repeatedVariantIds(items) {
    const ids = [];
    items.forEach((item) => {
      const id = item && (item.id != null ? item.id : item.variant && item.variant.id);
      if (id == null || !/^\d+$/.test(String(id))) return;
      for (let index = 0; index < Math.max(0, integer(item.quantity, 1)); index += 1) ids.push(String(id));
    });
    return ids;
  }

  function quantityProblem(items) {
    const grouped = new Map();
    for (const item of items) {
      if (!item.variant) return 'Cette combinaison n’est pas disponible. Choisissez une autre option.';
      if (item.variant.available === false) return 'Une variante choisie est en rupture de stock. Choisissez une autre option.';
      const key = String(item.variant.id);
      const existing = grouped.get(key) || { variant: item.variant, quantity: 0 };
      existing.quantity += item.quantity;
      grouped.set(key, existing);
    }
    for (const { variant, quantity } of grouped.values()) {
      const rules = variant.quantity_rule || {};
      const minimum = Math.max(1, integer(rules.min, 1));
      const increment = Math.max(1, integer(rules.increment, 1));
      if (quantity < minimum) return `Cette variante doit être commandée par ${minimum} articles au minimum.`;
      if ((quantity - minimum) % increment !== 0) return `Cette variante doit être commandée par multiples de ${increment} articles à partir de ${minimum}.`;
      if (rules.max != null && quantity > integer(rules.max)) return 'La quantité maximale autorisée pour cette variante est dépassée.';
      if (variant.inventory_management && variant.inventory_policy !== 'continue' && variant.inventory_quantity != null && quantity > integer(variant.inventory_quantity)) return 'Le stock disponible ne permet pas cette quantité. Réduisez le lot ou choisissez une autre variante.';
    }
    return null;
  }

  const helpers = Object.freeze({ calculateTier, repeatedVariantIds, quantityProblem });
  globalThis.ComfyQuantityBreaksHelpers = helpers;
  if (typeof HTMLElement === 'undefined' || typeof customElements === 'undefined' || customElements.get('comfy-quantity-breaks')) return;

  class ComfyQuantityBreaks extends HTMLElement {
    constructor() {
      super();
      this.hasVariants = false;
      this.variantSelects = [];
      this.labels = [];
      this.updatePdpPrices = false;
      this._offerPrices = null;
      this.sequence = 0;
    }

    static calculateTier(prices, discount) { return calculateTier(prices, discount); }
    static repeatedVariantIds(items) { return repeatedVariantIds(items); }

    connectedCallback() {
      this.controller = new AbortController();
      if (document.readyState !== 'complete' && !customElements.get('product-form')) {
        document.addEventListener('DOMContentLoaded', () => this.boot(), { once: true, signal: this.controller.signal });
      } else queueMicrotask(() => this.boot());
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.unsubscribeVariant?.();
      this.unsubscribeVariant = null;
      if (this.productForm && this.productForm.handleSubmit === this.submitGuard) this.productForm.handleSubmit = this.originalSubmit;
      if (this.productForm && this.productForm.quantityBreaks === this) this.productForm.quantityBreaks = this.previousBreaks && this.previousBreaks.isConnected ? this.previousBreaks : null;
      this.ready = false;
    }

    boot() {
      if (!this.isConnected || this.ready || this.controller.signal.aborted) return;
      const dataElement = this.querySelector('script[data-product-json], script[type="application/json"]');
      if (!dataElement) return;
      try { this.data = JSON.parse(dataElement.textContent); } catch (error) { console.error('[quantity-offers] Product data could not be read.', error); return; }
      this.variants = this.data.variants || [];
      if (this.data.quantityRules) this.variants.forEach((variant) => {
        const rule = this.data.quantityRules[String(variant.id)];
        if (rule) variant.quantity_rule = rule;
      });
      this.config = this.data.config || this.data;
      this._offerPrices = {};
      this.tiers = list(this.querySelectorAll('[data-tier]'));
      if (!this.tiers.length || !this.variants.length) return;
      if (this.id && document.getElementById(this.id) !== this) {
        this.showError('Utilisez un seul bloc Offres par quantité pour ce produit.');
        return;
      }
      this.ready = true;
      this.form = document.getElementById(this.dataset.formId || `product-form-${this.dataset.section}`);
      this.productForm = this.form?.closest('product-form');
      this.idInput = this.form?.querySelector('input[name="id"]');
      this.submitButton = this.form?.querySelector('[type="submit"]');
      this.baseVariant = this.initialVariant();
      this.selectorMode = this.dataset.selectorMode || (this.dataset.variantMode === 'same' ? 'same_variant' : 'per_item');
      this.multi = this.selectorMode === 'per_item' && this.variants.length > 1;
      this.maxQuantity = Math.max(1, integer(this.dataset.maxQuantity || this.config.maxQuantity, 12), ...this.tiers.map((tier) => this.minimum(tier)));
      this.money = this.makeMoney();
      this.tiers.forEach((tier) => {
        tier.dataset.minimum = String(this.minimum(tier));
        const rows = tier.querySelector('[data-rows]');
        if (this.multi && rows) {
          if (this.isFreeOffer(tier)) {
            list(rows.querySelectorAll('[data-row]')).slice(this.minimum(tier)).forEach((row) => row.remove());
          }
          while (rows.querySelectorAll('[data-row]').length < this.minimum(tier)) {
            if (!this.appendRow(tier, this.baseVariant)) break;
          }
          list(rows.querySelectorAll('[data-row]')).forEach((row) => this.setRowVariant(row, this.baseVariant));
        }
        this.renumber(tier);
      });
      const checked = this.tiers.find((tier) => tier.querySelector('[data-tier-radio], input[type="radio"]')?.checked);
      const preferred = this.dataset.preselected || this.dataset.autoSelect || this.config.preselected;
      this.active = checked || this.tiers.find((tier) => this.tierIndex(tier) === String(preferred)) || this.tiers[0];
      this.bind();
      this.attachProductForm();
      this.selectTier(this.active);
      this.setAttribute('data-ready', 'true');
    }

    initialVariant() {
      const urlId = new URL(window.location.href).searchParams.get('variant');
      const ids = [urlId, this.idInput?.value, this.config.selectedVariantId, this.data.selectedVariantId];
      for (const id of ids) {
        const match = this.variants.find((variant) => String(variant.id) === String(id));
        if (match) return match;
      }
      return this.variants.find((variant) => variant.available !== false) || this.variants[0];
    }

    tierIndex(tier) { return tier.dataset.index || tier.dataset.tierIndex || String(this.tiers.indexOf(tier) + 1); }
    isFreeOffer(tier) { return tier.dataset.discountType === 'buy_x_get_y'; }
    minimum(tier) {
      if (this.isFreeOffer(tier)) {
        const paid = Math.max(1, integer(tier.dataset.buyQuantity, 1));
        const free = Math.max(0, integer(tier.dataset.freeQuantity));
        return paid + free;
      }
      return Math.max(1, integer(tier.dataset.qty || tier.dataset.quantity, 1));
    }
    allowMore(tier) { return !this.isFreeOffer(tier) && (tier.dataset.allowMore === 'true' || tier.dataset.allowMore === '1'); }

    tierQuantity(tier) {
      if (this.multi && tier.querySelector('[data-rows]')) return tier.querySelectorAll('[data-rows] [data-row]').length;
      if (this.isFreeOffer(tier)) return this.minimum(tier);
      return Math.max(this.minimum(tier), integer(tier.querySelector('[data-qty-input]')?.value, this.minimum(tier)));
    }

    get selectedQuantity() {
      const tier = this.active || this.querySelector('[data-tier-radio]:checked')?.closest('[data-tier]');
      return tier ? this.tierQuantity(tier) : 1;
    }
    get selectedOffer() {
      if (this.active) return integer(this.tierIndex(this.active), 1);
      return integer(this.querySelector('[data-tier-radio]:checked')?.value || this.dataset.preselected, 1);
    }
    get offerPrices() {
      if (this._offerPrices) return this._offerPrices;
      // Optional subscription components can ask for totals before DOMContentLoaded.
      let data = this.data;
      if (!data) {
        try { data = JSON.parse(this.querySelector('script[data-product-json]')?.textContent || '{}'); }
        catch (_) { data = {}; }
      }
      const variant = (data.variants || []).find((item) => String(item.id) === String(data.selectedVariantId)) || data.variants?.[0];
      const result = {};
      list(this.querySelectorAll('[data-tier]')).forEach((tier, index) => {
        const quantity = this.minimum(tier);
        const total = Math.max(0, integer(variant?.price)) * quantity;
        result[`offer_${tier.dataset.index || tier.dataset.tierIndex || index + 1}`] = { price: total, compare_price: total };
      });
      if (!result.offer_1) result.offer_1 = { price: Math.max(0, integer(variant?.price)), compare_price: Math.max(0, integer(variant?.price)) };
      return result;
    }
    get formVariants() { return this.ready ? repeatedVariantIds(this.lineItems(this.active)) : []; }

    findVariant(values) {
      return this.variants.find((variant) => values.every((value, index) => String(variant.options[index]) === String(value))) || null;
    }

    rowVariant(row) {
      const selects = list(row.querySelectorAll('select[data-option-index], select[data-option-position]'));
      if (!selects.length) return this.baseVariant;
      const values = [];
      selects.forEach((select) => { values[integer(select.dataset.optionIndex ?? select.dataset.optionPosition)] = select.value; });
      return this.findVariant(values);
    }

    lineItems(tier) {
      if (!tier) return [];
      if (this.multi && tier.querySelector('[data-rows]')) return list(tier.querySelectorAll('[data-rows] [data-row]')).map((row) => ({ variant: this.rowVariant(row), quantity: 1 }));
      return [{ variant: this.baseVariant, quantity: this.tierQuantity(tier) }];
    }

    fixedCurrency() {
      const base = this.data.baseCurrency || this.data.currencyBase || this.data.currency || '';
      const active = window.Shopify?.currency?.active || this.data.activeCurrency || this.data.currencyActive || base;
      if (!base || !active || base === active) return { rate: 1, fixedAllowed: true };
      const rate = Number(window.Shopify?.currency?.rate);
      return { rate, fixedAllowed: Number.isFinite(rate) && rate > 0 };
    }

    pricing(tier) {
      const prices = [];
      this.lineItems(tier).forEach((item) => {
        for (let count = 0; count < item.quantity; count += 1) prices.push(item.variant ? item.variant.price : this.baseVariant.price);
      });
      return calculateTier(prices, {
        type: tier.dataset.discountType,
        value: tier.dataset.discountValue,
        buyQuantity: tier.dataset.buyQuantity,
        freeQuantity: tier.dataset.freeQuantity,
        ready: this.dataset.discountsReady === 'true',
        ...this.fixedCurrency()
      });
    }

    makeMoney() {
      const base = this.data.baseCurrency || this.data.currencyBase || this.data.currency || '';
      const active = window.Shopify?.currency?.active || this.data.activeCurrency || this.data.currencyActive || base || 'GBP';
      const format = this.data.moneyFormat || this.dataset.moneyFormat || '';
      const locale = document.documentElement.lang || 'fr';
      const fallback = (cents) => {
        try { return new Intl.NumberFormat(locale, { style: 'currency', currency: active }).format(cents / 100); }
        catch (_) { return (cents / 100).toFixed(2); }
      };
      return (cents) => {
        if (base && active !== base) return fallback(cents);
        const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
        if (!match) return fallback(cents);
        const formats = {
          amount: [2, ',', '.'], amount_no_decimals: [0, ',', '.'],
          amount_with_comma_separator: [2, '.', ','], amount_no_decimals_with_comma_separator: [0, '.', ','],
          amount_with_apostrophe_separator: [2, "'", '.'], amount_no_decimals_with_space_separator: [0, ' ', ','],
          amount_with_space_separator: [2, ' ', ','], amount_with_period_and_space_separator: [2, ' ', '.']
        };
        const [precision, thousands, decimal] = formats[match[1]] || formats.amount;
        const parts = (cents / 100).toFixed(precision).split('.');
        const value = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`) + (parts[1] ? decimal + parts[1] : '');
        const text = document.createElement('span');
        text.innerHTML = format.replace(match[0], value);
        return text.textContent;
      };
    }

    setRowVariant(row, variant) {
      if (!variant) return;
      list(row.querySelectorAll('select[data-option-index], select[data-option-position]')).forEach((select) => {
        const value = variant.options[integer(select.dataset.optionIndex ?? select.dataset.optionPosition)];
        if (list(select.options).some((option) => option.value === String(value))) select.value = String(value);
      });
      this.refreshRow(row);
    }

    refreshRow(row) {
      const variant = this.rowVariant(row);
      row.dataset.selectedId = variant ? String(variant.id) : '';
      row.classList.toggle('is-unavailable', !variant || variant.available === false);
      const image = row.querySelector('[data-row-img], [data-item-image]');
      const source = variant?.featured_image?.src || variant?.featured_image || this.data.featuredImage;
      if (image && typeof source === 'string' && source) {
        image.src = source + (source.includes('?') ? '&' : '?') + 'width=160';
        image.alt = variant?.title === 'Default Title' ? '' : variant?.title || '';
      }
      const selects = list(row.querySelectorAll('select[data-option-index], select[data-option-position]'));
      const values = selects.map((select) => select.value);
      selects.forEach((select) => {
        const index = integer(select.dataset.optionIndex ?? select.dataset.optionPosition);
        list(select.options).forEach((option) => {
          const exists = this.variants.some((candidate) => candidate.available !== false && String(candidate.options[index]) === option.value && values.slice(0, index).every((value, position) => String(candidate.options[position]) === value));
          option.disabled = !exists;
        });
      });
    }

    appendRow(tier, variant) {
      const container = tier.querySelector('[data-rows]');
      const template = tier.querySelector('template[data-row-template]') || this.querySelector('template[data-row-template]');
      if (!container || !template) return false;
      const row = template.content.firstElementChild?.cloneNode(true);
      if (!row) return false;
      const unique = `${this.id}-${this.tierIndex(tier)}-extra-${++this.sequence}`;
      list(row.querySelectorAll('[id]')).forEach((node, index) => {
        const oldId = node.id;
        const newId = `${unique}-${index}`;
        list(row.querySelectorAll('label[for]')).forEach((label) => { if (label.htmlFor === oldId) label.htmlFor = newId; });
        node.id = newId;
      });
      container.appendChild(row);
      this.setRowVariant(row, variant || this.baseVariant);
      return true;
    }

    renumber(tier) {
      const rows = list(tier.querySelectorAll('[data-rows] [data-row]'));
      rows.forEach((row, index) => {
        row.dataset.itemIndex = String(index + 1);
        const number = row.querySelector('[data-row-num]');
        if (number) number.textContent = String(index + 1);
        const remove = row.querySelector('[data-remove-row]');
        if (remove) remove.hidden = !this.allowMore(tier) || rows.length <= this.minimum(tier);
        list(row.querySelectorAll('select')).forEach((select) => { select.setAttribute('aria-label', `${select.dataset.optionName || 'Option'} — article ${index + 1}`); });
      });
      const input = tier.querySelector('[data-qty-input]');
      if (input) {
        input.min = String(this.minimum(tier));
        input.max = String(this.isFreeOffer(tier) ? this.minimum(tier) : this.maxQuantity);
        input.readOnly = this.isFreeOffer(tier);
        if (this.isFreeOffer(tier)) input.value = String(this.minimum(tier));
        input.step = String(Math.max(1, integer(this.baseVariant?.quantity_rule?.increment, 1)));
      }
      const add = tier.querySelector('[data-add-row], [data-qty-plus]');
      if (add) add.disabled = this.tierQuantity(tier) >= this.maxQuantity;
      const minus = tier.querySelector('[data-qty-minus]');
      if (minus) minus.disabled = this.tierQuantity(tier) <= this.minimum(tier);
    }

    refresh() {
      this.tiers.forEach((tier) => {
        this.renumber(tier);
        list(tier.querySelectorAll('[data-rows] [data-row]')).forEach((row) => this.refreshRow(row));
        const price = this.pricing(tier);
        const freeIndices = new Set(price.freeIndices);
        list(tier.querySelectorAll('[data-rows] [data-row]')).forEach((row, index) => {
          const isFree = freeIndices.has(index);
          row.classList.toggle('is-free', isFree);
          const badge = row.querySelector('[data-free-item]');
          if (badge) { badge.textContent = this.config.labels?.freeItem || 'Offert'; badge.hidden = !isFree; }
        });
        this.offerPrices[`offer_${this.tierIndex(tier)}`] = { price: price.final, compare_price: price.total };
        const total = tier.querySelector('[data-price-new]');
        const old = tier.querySelector('[data-price-old]');
        const unit = tier.querySelector('[data-price-unit]');
        if (total) total.textContent = this.money(price.final);
        if (old) { old.textContent = this.money(price.total); old.hidden = price.saving <= 0 || this.config.showCompare === false; }
        if (unit) { unit.textContent = `${this.money(price.unit)} ${this.config.labels?.unit || 'par article'}`; unit.hidden = this.config.showUnitPrice !== true; }
        const label = tier.querySelector('[data-tier-label]');
        if (label) {
          label.dataset.label = label.dataset.label || label.textContent;
          const tokens = {
            quantity: String(price.quantity) + (this.allowMore(tier) ? '+' : ''),
            total: String(price.quantity),
            paid: String(price.quantity - price.qualifiedFreeCount),
            free: String(price.qualifiedFreeCount)
          };
          const template = this.isFreeOffer(tier) && this.dataset.discountsReady !== 'true'
            ? this.config.labels?.pendingOffer || '[total] articles' : label.dataset.label;
          const parts = template.split(/(\[(?:quantity|paid|free|total)\])/g);
          label.replaceChildren();
          parts.forEach((part) => {
            const token = part.match(/^\[(quantity|paid|free|total)\]$/);
            if (token) {
              const quantity = document.createElement('span');
              quantity.className = 'comfy-qb__quantity';
              quantity.textContent = tokens[token[1]];
              label.appendChild(quantity);
            } else label.appendChild(document.createTextNode(part));
          });
        }
        const saving = tier.querySelector('[data-saving]');
        if (saving) {
          const type = tier.dataset.discountType;
          const labels = this.config.labels || {};
          const template = type === 'buy_x_get_y' ? labels.freeSaving || '[free] article(s) offert(s)' : type === 'percent' ? labels.percentSaving || 'Économisez [discount]%' : type === 'fixed_total' ? labels.totalSaving || '[saving] de réduction sur le lot' : labels.fixedSaving || '[saving] de réduction par article';
          const amount = type === 'fixed_item' ? Math.round(price.saving / Math.max(1, price.quantity)) : price.saving;
          saving.textContent = template.replace(/\[saving\]/g, this.money(Number.isFinite(amount) ? amount : 0)).replace(/\[discount\]/g, String(Math.min(100, positive(tier.dataset.discountValue)))).replace(/\[free\]/g, String(price.freeCount)).replace(/\[paid\]/g, String(price.paidQuantity)).replace(/\[total\]/g, String(price.quantity)).replace(/\[quantity\]/g, String(price.quantity));
          saving.hidden = type === 'buy_x_get_y' ? price.freeCount <= 0 : price.saving <= 0;
        }
      });
      this.syncForm();
      this.validate(false);
      const gifts = document.getElementById(`quantity-gifts-${this.dataset.section}`);
      if (gifts && typeof gifts.unlockGifts === 'function') gifts.unlockGifts(this.selectedQuantity);
    }

    selectTier(tier) {
      this.active = tier;
      this.tiers.forEach((candidate) => {
        const active = candidate === tier;
        candidate.classList.toggle('is-active', active);
        const radio = candidate.querySelector('[data-tier-radio], input[type="radio"]');
        if (radio) radio.checked = active;
        const body = candidate.querySelector('[data-tier-body], .comfy-qb__tier-body');
        if (body) body.hidden = !active;
      });
      this.refresh();
    }

    syncForm() {
      const first = this.lineItems(this.active)[0]?.variant;
      if (this.idInput && first) { this.idInput.disabled = false; this.idInput.value = String(first.id); }
      if (!this.submitButton || !first || first.available === false) return;
      if (this.submitButton.dataset.unavailable === 'true') {
        this.submitButton.dataset.unavailable = 'false';
        if (this.submitButton.dataset.requiredFields === this.submitButton.dataset.validFields) this.submitButton.removeAttribute('disabled');
        const label = this.submitButton.querySelector('.main-atc__label__text, .main-atc__label');
        if (label) label.textContent = this.submitButton.dataset.customLabel || window.variantStrings?.addToCart || 'Ajouter au panier';
      }
    }

    showError(message) {
      const error = this.querySelector('[data-offer-error]');
      if (error) { error.textContent = message || ''; error.hidden = !message; }
    }

    validate(show = true) {
      let problem = this.selectedQuantity > this.maxQuantity ? `Le lot peut contenir au maximum ${this.maxQuantity} articles.` : quantityProblem(this.lineItems(this.active));
      if (this.active && this.isFreeOffer(this.active) && this.selectedQuantity !== this.minimum(this.active)) problem = `Ce lot doit contenir exactement ${this.minimum(this.active)} articles, articles offerts compris.`;
      if (!this.form) problem = 'Le formulaire produit est indisponible.';
      this.invalid = !!problem;
      this.classList.toggle('is-invalid', this.invalid);
      if (show || !problem) this.showError(problem);
      return !problem;
    }

    guardSubmission(event) {
      if (this.validate()) return true;
      event?.preventDefault();
      event?.stopImmediatePropagation();
      this.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      return false;
    }

    attachProductForm() {
      if (!this.productForm || !this.form) return;
      this.previousBreaks = this.productForm.quantityBreaks;
      this.productForm.quantityBreaks = this;
      this.productForm.quantityBreakUpsells = this.querySelectorAll('quantity-break-upsells');
      this.form.addEventListener('submit', (event) => this.guardSubmission(event), { capture: true, signal: this.controller.signal });
      this.originalSubmit = this.productForm.handleSubmit;
      if (typeof this.originalSubmit === 'function') {
        const widget = this;
        this.submitGuard = function (...args) {
          if (widget.isConnected && !widget.guardSubmission(args[0])) return;
          return widget.originalSubmit.apply(this, args);
        };
        this.productForm.handleSubmit = this.submitGuard;
      }
    }

    changeMainVariant(variant) {
      if (!variant || String(variant.id) === String(this.baseVariant.id)) return;
      this.baseVariant = this.variants.find((item) => String(item.id) === String(variant.id)) || variant;
      if (this.multi) this.tiers.forEach((tier) => list(tier.querySelectorAll('[data-rows] [data-row]')).forEach((row) => this.setRowVariant(row, this.baseVariant)));
      this.refresh();
    }

    bind() {
      const signal = this.controller.signal;
      this.addEventListener('change', (event) => {
        const tier = event.target.closest('[data-tier]');
        if (!tier) return;
        if (event.target.matches('[data-tier-radio], input[type="radio"]')) { this.selectTier(tier); return; }
        if (event.target.matches('select[data-option-index], select[data-option-position]')) { this.refresh(); return; }
        if (event.target.matches('[data-qty-input]')) {
          event.target.value = String(Math.min(this.maxQuantity, Math.max(this.minimum(tier), integer(event.target.value, this.minimum(tier)))));
          this.refresh();
        }
      }, { signal });
      this.addEventListener('click', (event) => {
        const target = event.target.closest('[data-add-row], [data-remove-row], [data-qty-plus], [data-qty-minus]');
        if (!target) return;
        event.preventDefault();
        const tier = target.closest('[data-tier]');
        if (!tier || !this.allowMore(tier)) return;
        if (target.matches('[data-add-row]') && this.tierQuantity(tier) < this.maxQuantity) {
          const rows = tier.querySelectorAll('[data-rows] [data-row]');
          this.appendRow(tier, rows.length ? this.rowVariant(rows[rows.length - 1]) : this.baseVariant);
        } else if (target.matches('[data-remove-row]') && this.tierQuantity(tier) > this.minimum(tier)) target.closest('[data-row]').remove();
        else if (target.matches('[data-qty-plus], [data-qty-minus]')) {
          const input = tier.querySelector('[data-qty-input]');
          const step = Math.max(1, integer(this.baseVariant.quantity_rule?.increment, 1));
          if (input) input.value = String(Math.min(this.maxQuantity, Math.max(this.minimum(tier), this.tierQuantity(tier) + (target.matches('[data-qty-plus]') ? step : -step))));
        }
        this.refresh();
      }, { signal });
      this.idInput?.addEventListener('change', () => {
        if (!this.multi) this.changeMainVariant(this.variants.find((variant) => String(variant.id) === this.idInput.value));
      }, { signal });
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (String(event.data?.sectionId) === this.dataset.section) this.changeMainVariant(event.data.variant);
        });
      }
    }

    // Compatibility with existing optional subscription/variant components.
    initOfferPrices() { if (this.ready) this.refresh(); }
    initVariants() { if (this.ready) this.refresh(); }
    updateInnerPrices() { if (this.ready) this.refresh(); }
    updateDisplayedPdpPrices() { if (this.ready) this.refresh(); }
  }

  customElements.define('comfy-quantity-breaks', ComfyQuantityBreaks);
})();
